/**
 * Inject locked-feature hints in the sidebar menu.
 *
 * Strategy: dopo che Layout.tsx ha già filtrato il menu in base ai permission,
 * questa funzione confronta `filteredMenu` con `originalMenu` e re-inietta
 * (come voci "locked") le item presenti in originalMenu ma assenti in filteredMenu
 * SE:
 *  - la loro chiave compare in LOCKED_FEATURES_REGISTRY
 *  - l'ORG NON ha già la feature attiva (plan o addon)
 *
 * Discriminante: l'inject avviene solo per feature che l'ORG non possiede
 * (sblocco = acquisto/upgrade). Se l'org HA la feature ma il role del singolo
 * utente NON ha permission, la voce resta nascosta (è una decisione interna
 * dell'admin: l'utente non ha modo di "sbloccarla" autonomamente).
 *
 * Disabilitabile via env: VITE_FEATURE_LOCKED_DISCOVERY=false
 */

import type { MenuItem } from '../types';
import {
  LOCKED_FEATURES_REGISTRY,
  isLockedDiscoveryEnabled,
} from '../config/lockedFeaturesRegistry';

/** Walk ricorsivo: estrae tutti gli id di un menu (children incluse). */
function collectIds(items: MenuItem[]): Set<string> {
  const ids = new Set<string>();
  for (const it of items) {
    ids.add(it.id);
    if (it.children?.length) {
      for (const id of collectIds(it.children)) ids.add(id);
    }
  }
  return ids;
}

/** Trova l'item con id `targetId` dentro `tree` (depth-first). */
function findItem(tree: MenuItem[], targetId: string): MenuItem | null {
  for (const it of tree) {
    if (it.id === targetId) return it;
    if (it.children?.length) {
      const nested = findItem(it.children, targetId);
      if (nested) return nested;
    }
  }
  return null;
}

/** Trova il parent dell'item con id `childId` in `tree` (depth-first). */
function findParent(tree: MenuItem[], childId: string): MenuItem | null {
  for (const it of tree) {
    if (it.children?.some((c) => c.id === childId)) return it;
    if (it.children?.length) {
      const nested = findParent(it.children, childId);
      if (nested) return nested;
    }
  }
  return null;
}

/**
 * Inietta nelle posizioni corrette (dentro la parent originale) le voci
 * locked che esistono in originalMenu ma sono state filtrate via.
 *
 * @param filteredMenu menu già filtrato per permission
 * @param originalMenu menu integro (MENU_CONFIG)
 * @param orgHasFeature callback opzionale: data una moduleKey, ritorna true se
 *   l'org ha la feature attiva (via plan.enabled_modules o organization_addons).
 *   Se la feature è attiva sull'org, la voce NON viene injettata come locked —
 *   significa che è solo role-locked (l'admin l'ha esclusa dal ruolo personale)
 *   e mostrarla come 🔒 sarebbe fuorviante (l'utente non può sbloccarla da solo).
 *   Se non passato, comportamento legacy: inietta tutte le feature in registry
 *   non visibili dal filter (assume che tutto sia org-locked).
 * @param roleCanView callback opzionale: data la featureKey, ritorna true se il
 *   RUOLO dell'utente avrebbe il permesso di vedere la sezione. Se false, l'hint
 *   locked NON viene mostrato: una sezione esclusa dal ruolo non deve comparire
 *   nemmeno come "comprabile" (l'utente non potrebbe usarla dopo lo sblocco).
 * @returns nuovo array con voci locked aggiunte (no mutation in place)
 */
export function injectLockedFeatures(
  filteredMenu: MenuItem[],
  originalMenu: MenuItem[],
  orgHasFeature?: (moduleKey: string) => boolean,
  roleCanView?: (featureKey: string) => boolean
): MenuItem[] {
  if (!isLockedDiscoveryEnabled()) return filteredMenu;

  const filteredIds = collectIds(filteredMenu);

  // Deep clone superficiale del tree (per non mutare l'input)
  const clone = (items: MenuItem[]): MenuItem[] =>
    items.map((it) => ({
      ...it,
      children: it.children ? clone(it.children) : undefined,
    }));
  const result = clone(filteredMenu);

  for (const featureKey of Object.keys(LOCKED_FEATURES_REGISTRY)) {
    // Già visibile? Skip.
    if (filteredIds.has(featureKey)) continue;

    const meta = LOCKED_FEATURES_REGISTRY[featureKey];
    const originalItem = findItem(originalMenu, featureKey);
    if (!originalItem) continue; // Feature in registry ma non in menuConfig: skip.

    // Se l'org HA già la feature → voce filtrata via per role-permission, non
    // per assenza di addon/piano. Non mostriamo locked (sarebbe fuorviante).
    if (orgHasFeature) {
      // moduleKey della registry oppure derivata da featureKey (dash → underscore)
      const moduleKey = meta.moduleKey || featureKey.replace(/-/g, '_');
      if (orgHasFeature(moduleKey)) continue;
    }

    // Se il ruolo non ha il permesso di vista sulla sezione, niente hint:
    // anche dopo l'acquisto l'utente non la vedrebbe.
    if (roleCanView && !roleCanView(featureKey)) continue;

    // Mappa addonGroup → AddonId per il modal dedicato
    const ADDON_GROUP_TO_ID: Record<string, 'warehouse' | 'newsletter'> = {
      warehouse: 'warehouse',
      newsletter: 'newsletter',
    };
    const lockedAddon =
      meta.unlockBy === 'addon' && meta.addonGroup
        ? ADDON_GROUP_TO_ID[meta.addonGroup]
        : undefined;

    // Costruisci la versione "locked" della voce
    const lockedItem: MenuItem = {
      ...originalItem,
      // Sostituisci target route con /unlock/:featureKey
      to: `/unlock/${featureKey}`,
      icon: meta.icon || originalItem.icon,
      // Override resource/action così la sidebar non riprova il check
      resource: undefined,
      action: undefined,
      children: undefined,
      _locked: true,
      _lockedBadge:
        meta.unlockBy === 'addon'
          ? 'Add-on'
          : meta.minPlanName || 'Upgrade',
      // Se è un addon con modal dedicato, imposta _lockedAddon per intercettare il click
      ...(lockedAddon ? { _lockedAddon: lockedAddon } : {}),
    };

    // Inserisci nella parent corretta (preserva la struttura del menuConfig).
    const originalParent = findParent(originalMenu, featureKey);
    if (originalParent) {
      // Trova la parent corrispondente nel result tree.
      const parentInResult = findItem(result, originalParent.id);
      if (parentInResult) {
        parentInResult.children = parentInResult.children || [];
        // Evita duplicati (paranoia)
        if (!parentInResult.children.some((c) => c.id === featureKey)) {
          parentInResult.children.push(lockedItem);
        }
      } else {
        // Parent stessa non visibile (es. categoria intera filtrata) → metto la voce
        // come top-level. Caso edge raro ma gestito.
        result.push(lockedItem);
      }
    } else {
      // Top-level item filtered out → metto come top-level
      result.push(lockedItem);
    }
  }

  return result;
}
